import space from "color-space";

export const spaceToSpace = (
  numbers: number[],
  from: string,
  to: string
): number[] => {
  const array = space[from][to](numbers).map((number: number) =>
    Math.round(number)
  );
  return array;
};

export const parseToRgb = (string: string): number[] | null => {
  /* define regex to match CSS string format for hex, rgb, and hsl */
  const regex =
    /#([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})(?:\s|$)|rgb\((\d+),\s*([\d.]+),\s*([\d.]+)\)|hsl\((\d+),\s*([\d.]+)%,\s*([\d.]+)%\)/;

  /* get array of matches (whole matches and capturing groups) */
  let matches = string.match(regex);

  /* if no match, return null */
  if (matches === null || !matches[0]) {
    return null;
  }

  /* keep only the values for the whole match and its capturing groups */
  matches = matches.filter((match) => match && match !== undefined);

  /* if no matches left, return null */
  if (!matches[0]) {
    return null;
  }

  /* destructure whole match (color) and the rest (values) */
  const [color, ...values] = matches;

  /* parse the integer from each string (base 16 for hex codes, base 10 otherwise */
  const integers = values.map((value) =>
    parseInt(value, color.startsWith("#") ? 16 : 10)
  );

  /* if color is hex or rgb... */
  if (color.startsWith("#") || color.startsWith("rgb")) {
    /* check that all values are 0-255 */
    const invalid = integers.some((integer) => integer < 0 || integer > 255);

    /* return null or integers, depending on the case */
    return invalid ? null : integers;
  }

  /* if color is hsl... */
  if (color.startsWith("hsl")) {
    /* check that the first value is 0-360, and that the others are 0-100*/
    const invalid = integers.some(
      (integer, i) =>
        integer < 0 || (i === 0 && integer > 360) || (i > 0 && integer > 100)
    );

    /* return null or object, depending on the case */
    return invalid ? null : spaceToSpace(integers, "hsl", "rgb");
  }

  return null;
};

export const getRelativeLuminance = (rgb: number[]): number => {
  /* normalize each channel to a 0-1 range, instead of 0-255 */
  let newRgb = rgb.map((channel) => channel / 255);

  /* transform into a curve that better fits human color perception */
  newRgb = newRgb.map((channel) =>
    channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
  );

  /* adjust the contribution of each color channel, based on how much intensity it provides to the human eye */
  const [r, g, b] = newRgb;
  const Y = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  return Y;
};

export const getContrast = (
  rgb1: number[],
  rgb2: number[],
  decimals = 1
): string => {
  /* get relative luminance of each color */
  const Y1 = getRelativeLuminance(rgb1);
  const Y2 = getRelativeLuminance(rgb2);

  /* get max and min values */
  const max = Math.max(Y1, Y2);
  const min = Math.min(Y1, Y2);

  /* divide max by min (0.05 is added to prevent dividing by 0) */
  const contrast = (max + 0.05) / (min + 0.05);

  return contrast.toFixed(decimals);
};

export const chooseBlackOrWhite = (color: string): string => {
  const black = "#000000";
  const white = "#FFFFFF";
  const rgb = parseToRgb(color);
  if (!rgb) {
    return black;
  }
  const luminance = getRelativeLuminance(rgb);
  return luminance < 0.19 ? white : black;
};

export const rgbToHex = (rgb: number[]): string => {
  /* map over channels */
  const hexes = rgb.map((channel) => {
    /* round each channel and convert to a hex value */
    let hex = Math.round(channel).toString(16);

    /* if only one character, add an initial 0 */
    hex = hex.length === 2 ? hex : "0" + hex;

    return hex;
  });

  const string = "#" + hexes[0] + hexes[1] + hexes[2];

  return string;
};

export const hsluvToHex = (hsluv: number[]): string => {
  return rgbToHex(spaceToSpace(hsluv, "hsluv", "rgb"));
};
